import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import StatusChip from "@/components/StatusChip";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Download, Inbox, Mail, Paperclip, Search, Plus, PenSquare, Sheet, RefreshCw, Settings, History, CalendarClock, CheckCircle2, ClipboardList, Timer, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["all", "New from Gmail", "Pending CAD Input", "Pending Production Input", "Ready for Review", "Approved", "Rejected"];

// Convert an ISO timestamp to "DD Mon YYYY · HH:mm IST"
function fmtIst(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).replace(",", " ·") + " IST";
}

// Format YYYY-MM-DD (or ISO) as "DD Mon YYYY" — no time.
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(String(iso).length <= 10 ? iso + "T00:00:00Z" : iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" });
}

// Was the target date missed? (today's IST date > target date and enquiry not yet closed)
function isOverdue(targetDate, sentForApprovalAt) {
  if (!targetDate) return false;
  if (sentForApprovalAt) return false;
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }); // YYYY-MM-DD
  return today > String(targetDate).slice(0, 10);
}

function statusLabel(e) {
  if (["Approved", "Rejected"].includes(e.status)) return e.status;
  if (e.sent_for_approval_at) return "Ready for Approval";
  const cad = !!e.cad_submitted_at, prod = !!e.production_submitted_at;
  if (cad && prod) return "Ready for Approval";
  if (cad && !prod) return "CAD ✓ · Awaiting Production";
  if (!cad && prod) return "Prod ✓ · Awaiting CAD";
  return "Awaiting CAD & Production";
}

function StatusCell({ e }) {
  const cad = !!e.cad_submitted_at;
  const prod = !!e.production_submitted_at;
  const merch = !!e.sent_for_approval_at;
  const done = e.status === "Approved";
  const rejected = e.status === "Rejected";
  const chip = (label, ok, extra = "") => (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-semibold border ${ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"} ${extra}`}>
      {ok ? "✓" : "○"} {label}
    </span>
  );
  return (
    <div className="flex flex-col gap-1" data-testid={`status-cell-${e.enquiry_number}`}>
      <div className="flex flex-wrap gap-1">
        {chip("CAD", cad)}
        {chip("Prod", prod)}
        {chip("Merch", merch)}
      </div>
      {done && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-600 text-white text-[10.5px] font-bold w-fit">APPROVED</span>}
      {rejected && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-600 text-white text-[10.5px] font-bold w-fit">REJECTED</span>}
    </div>
  );
}

export default function EnquiryList() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [status, setStatus] = useState("all");
  const [buyer, setBuyer] = useState("all");
  const [q, setQ] = useState("");
  const [kpiFilter, setKpiFilter] = useState("all"); // "all" | "closed" | "open" | "timely"
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [form, setForm] = useState({ enquiry_number: "", buyer: "", buyer_style: "", notes: "" });
  const [sheetSettings, setSheetSettings] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetUrlDraft, setSheetUrlDraft] = useState("");
  const [syncBusy, setSyncBusy] = useState(false);
  // Historical bulk import
  const [histOpen, setHistOpen] = useState(false);
  const [histForm, setHistForm] = useState({ headers_url: "", line_items_url: "", wipe_first: false });
  const [histReport, setHistReport] = useState(null);
  const [histBusy, setHistBusy] = useState(false);

  const canCreate = (user?.role === "admin" || user?.role === "admin_addl"); // Emergency stop-gap only — Sheet sync is primary
  const canSync = (user?.role === "admin" || user?.role === "admin_addl") || user?.role === "merchandiser";
  const isAdmin = (user?.role === "admin" || user?.role === "admin_addl");

  // Inline target-date editing (admin / admin_addl only). The Google Sheet sync only
  // backfills EMPTY target dates, so a manual change here is never overwritten.
  const [editingTargetId, setEditingTargetId] = useState(null);
  const [targetDraft, setTargetDraft] = useState("");
  const saveTargetDate = async (e) => {
    try {
      await api.patch(`/enquiries/${e.id}`, { target_date: targetDraft || null });
      toast.success(`Target date updated for ${e.enquiry_number}`);
      setEditingTargetId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Target date update failed");
    }
  };

  // Column sorting (persisted in localStorage per browser)
  const SORT_KEY = "opsiq_enq_sort";
  const [sort, setSort] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SORT_KEY) || "null") || { col: "updated_at", dir: "desc" }; }
    catch { return { col: "updated_at", dir: "desc" }; }
  });
  useEffect(() => { localStorage.setItem(SORT_KEY, JSON.stringify(sort)); }, [sort]);
  const toggleSort = (col) => setSort((s) => s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: col === "updated_at" || col === "created_at" ? "desc" : "asc" });
  const sortIcon = (col) => sort.col !== col ? "" : (sort.dir === "asc" ? " ↑" : " ↓");

  const load = async () => {
    try {
      const params = {};
      if (status !== "all") params.status_filter = status;
      if (buyer !== "all") params.buyer = buyer;
      const [{ data }, statsRes] = await Promise.all([
        api.get("/enquiries", { params }),
        api.get("/enquiries/stats").catch(() => ({ data: null })),
      ]);
      setItems(data);
      if (statsRes?.data) setStats(statsRes.data);
    } catch { toast.error("Failed to load enquiries"); }
  };

  useEffect(() => { (async () => {
    const b = await api.get("/masters/buyers"); setBuyers(b.data);
    try { const s = await api.get("/masters/enquiry-sheet"); setSheetSettings(s.data); setSheetUrlDraft(s.data.url || ""); } catch {}
  })(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, buyer]);

  const saveSheetUrl = async () => {
    try {
      const { data } = await api.put("/masters/enquiry-sheet", { url: sheetUrlDraft.trim() });
      setSheetSettings(data);
      toast.success("Sheet URL saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const runHistoricalImport = async (dry_run) => {
    if (!histForm.headers_url.trim()) { toast.error("Headers sheet URL is required"); return; }
    setHistBusy(true);
    try {
      const { data } = await api.post("/enquiries/import/historical", { ...histForm, dry_run });
      setHistReport(data);
      if (dry_run) {
        toast.success("Dry run complete — review the report below");
      } else {
        toast.success(`Import complete — created ${data.created}, updated ${data.updated}`);
        load();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    setHistBusy(false);
  };

  const syncFromSheet = async () => {
    if (!sheetSettings?.url) {
      toast.error("Set the Google Sheet URL first", { description: "Click the gear icon next to Sync to configure it." });
      setSheetOpen(true);
      return;
    }
    setSyncBusy(true);
    try {
      const { data } = await api.post("/enquiries/sync-from-sheet");
      toast.success(`Sync complete`, {
        description: `Created ${data.created} · Skipped ${data.skipped_existing} existing · ${data.skipped_invalid} invalid`,
      });
      load();
      const s = await api.get("/masters/enquiry-sheet"); setSheetSettings(s.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    }
    setSyncBusy(false);
  };

  const filtered = useMemo(() => {
    let list = items;
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((e) => [e.enquiry_number, e.buyer, e.buyer_style, e.email_subject].some((x) => x?.toLowerCase().includes(s)));
    }
    // KPI tile filter — composes AND with search/buyer/status dropdown filters.
    // Mirrors the backend logic in `/enquiries/stats` (server.py enquiry_stats):
    //   closed  = has sent_for_approval_at
    //   open    = !sent_for_approval_at
    //   timely  = closed & has target_date & sent_for_approval_at[:10] <= target_date
    if (kpiFilter !== "all") {
      list = list.filter((e) => {
        const sfa = e.sent_for_approval_at;
        if (kpiFilter === "closed") return !!sfa;
        if (kpiFilter === "open") return !sfa;
        if (kpiFilter === "timely") {
          if (!sfa) return false;
          const tgt = (e.target_date || "").slice(0, 10);
          if (!tgt) return false;
          return String(sfa).slice(0, 10) <= tgt;
        }
        return true;
      });
    }
    // Sort — versions/dates numeric-aware
    const dir = sort.dir === "asc" ? 1 : -1;
    const col = sort.col;
    return [...list].sort((a, b) => {
      const va = a[col] ?? ""; const vb = b[col] ?? "";
      if (col === "version") return dir * ((Number(va) || 0) - (Number(vb) || 0));
      if (col === "attachments") return dir * (((a.attachments || []).length) - ((b.attachments || []).length));
      return dir * String(va).localeCompare(String(vb));
    });
  }, [items, q, sort, kpiFilter]);

  const exportCsv = () => {
    const headers = ["Enquiry #", "Buyer", "Buyer Style", "Target Date", "Initiated (IST)", "Last Updated (IST)", "Version", "Source", "Status", "Attachments"];
    const rows = filtered.map((e) => [e.enquiry_number, e.buyer, e.buyer_style, fmtDate(e.target_date), fmtIst(e.created_at || e.received_date), fmtIst(e.updated_at || e.received_date), e.version || 1, e.source || "email", statusLabel(e), (e.attachments || []).length]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `enquiries-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported CSV");
  };

  const doCreate = async () => {
    if (!form.buyer.trim() || !form.buyer_style.trim()) { toast.error("Buyer and Buyer Style are required"); return; }
    setCreateBusy(true);
    try {
      const { data } = await api.post("/enquiries", form);
      toast.success(`Created ${data.enquiry_number}`);
      setCreateOpen(false);
      setForm({ enquiry_number: "", buyer: "", buyer_style: "", notes: "" });
      nav(`/enquiries/${data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Create failed"); }
    setCreateBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="enquiries-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11.5px] font-mono tracking-widest text-brand uppercase inline-flex items-center gap-1.5"><Inbox className="h-3.5 w-3.5" /> Enquiry Costing</div>
          <h1 className="mt-1 font-heading font-bold text-3xl tracking-tight">Enquiry Inbox</h1>
          <p className="text-slate-500 text-[14px] mt-1">Synced from your Google Sheet or created manually. Fill costing, revise as needed, and send for approval.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={exportCsv} data-testid="enquiries-export-btn"><Download className="h-4 w-4 mr-2" /> Export CSV</Button>
          {canSync && (
            <div className="inline-flex items-center gap-0" data-testid="sheet-sync-group">
              <Button
                onClick={syncFromSheet}
                disabled={syncBusy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-r-none"
                data-testid="sync-from-sheet-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${syncBusy ? "animate-spin" : ""}`} /> {syncBusy ? "Syncing…" : "Sync from Sheet"}
              </Button>
              <Dialog open={sheetOpen} onOpenChange={setSheetOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-l-none border-l-0 px-2" data-testid="sheet-settings-btn" aria-label="Sheet settings">
                    <Settings className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="sheet-settings-dialog">
                  <DialogHeader><DialogTitle className="flex items-center gap-2"><Sheet className="h-5 w-5 text-emerald-600" /> Enquiry Google Sheet</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <p className="text-[12.5px] text-slate-500">
                      Paste the shareable Google Sheet URL. Set the share option to <b>"Anyone with the link — Viewer"</b>. Expected columns: <span className="font-mono">Enquiry Number, Buyer, Buyer Style, Received Date, Notes</span> (only Buyer and Buyer Style are required).
                    </p>
                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sheet URL {!isAdmin && <span className="text-slate-400 normal-case font-normal">· admin-only to edit</span>}</label>
                      <Input
                        value={sheetUrlDraft}
                        onChange={(e) => setSheetUrlDraft(e.target.value)}
                        placeholder="https://docs.google.com/spreadsheets/d/…/edit#gid=0"
                        data-testid="sheet-url-input"
                        disabled={!isAdmin}
                        className="mt-1 font-mono text-[12.5px]"
                      />
                    </div>
                    {sheetSettings?.last_sync_at && (
                      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-600" data-testid="last-sync-summary">
                        <div className="font-semibold text-slate-700 mb-1">Last sync</div>
                        {new Date(sheetSettings.last_sync_at).toLocaleString("en-GB", { timeZone: "Asia/Kolkata" })} IST · by {sheetSettings.last_sync_by}
                        {sheetSettings.last_sync_summary && (
                          <div className="mt-1 font-mono">
                            +{sheetSettings.last_sync_summary.created} new · {sheetSettings.last_sync_summary.skipped_existing} duplicates · {sheetSettings.last_sync_summary.skipped_invalid} invalid
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setSheetOpen(false)}>Close</Button>
                    {isAdmin && <Button onClick={saveSheetUrl} className="bg-brand hover:bg-brand-dark text-white" data-testid="sheet-url-save">Save URL</Button>}
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          {isAdmin && (
            <Dialog open={histOpen} onOpenChange={setHistOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="historical-import-btn">
                  <History className="h-4 w-4 mr-2" /> Historical Import
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl" data-testid="historical-import-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-amber-700" /> One-time Historical Import</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-900">
                    Paste two link-viewable Google Sheet URLs. Do a <b>Dry Run</b> first to preview what will be created/updated, then commit with <b>Import Now</b>. Line-items sheet is optional — omit it if you only want to import enquiry headers.
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Headers Sheet URL <span className="text-rose-600">*</span></label>
                    <Input value={histForm.headers_url} onChange={(ev) => setHistForm({ ...histForm, headers_url: ev.target.value })} placeholder="https://docs.google.com/spreadsheets/d/…/edit#gid=0" data-testid="hist-headers-url" className="mt-1 font-mono text-[12.5px]" />
                    <div className="text-[11px] text-slate-500 mt-1">Columns: <span className="font-mono">Enquiry Number, Buyer*, Buyer Style*, Received Date, Notes, Status</span></div>
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Line Items Sheet URL <span className="text-slate-400 normal-case font-normal">(optional)</span></label>
                    <Input value={histForm.line_items_url} onChange={(ev) => setHistForm({ ...histForm, line_items_url: ev.target.value })} placeholder="https://docs.google.com/spreadsheets/d/…/edit#gid=…" data-testid="hist-lines-url" className="mt-1 font-mono text-[12.5px]" />
                    <div className="text-[11px] text-slate-500 mt-1">Columns: <span className="font-mono">Enquiry Number, Section, Type/Item/Color, Position, Fabric Type, Width, UOM, Consumption, Tex/Details, Remark</span>. Section ∈ Fabric | Tapes | SewingThread | EmbroideryThread | Labour.</div>
                  </div>
                  <label className="inline-flex items-center gap-2 text-[13px] text-slate-700" data-testid="hist-wipe-first-label">
                    <input type="checkbox" checked={histForm.wipe_first} onChange={(ev) => setHistForm({ ...histForm, wipe_first: ev.target.checked })} className="accent-[#7C1D3A]" data-testid="hist-wipe-first" />
                    Wipe all existing sheet + manual + historical enquiries before importing (clean cutover)
                  </label>
                  {histReport && (
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-700 max-h-56 overflow-y-auto" data-testid="hist-report">
                      <div className="font-semibold text-slate-900 mb-1">{histReport.dry_run ? "Dry-run report" : "Commit report"}</div>
                      <div className="font-mono text-[11.5px]">
                        <div>Headers · to_create: {histReport.headers.to_create.length} · to_update: {histReport.headers.to_update.length} · approved: {histReport.headers.approved} · invalid: {histReport.headers.invalid}</div>
                        <div>Line items · matched: {histReport.line_items.matched} · invalid: {histReport.line_items.invalid} · unknown enquiries: {histReport.line_items.unknown_enquiry.length}</div>
                        <div className="pl-4">
                          Fabric: {histReport.line_items.by_section.Fabric} · Tapes: {histReport.line_items.by_section.Tapes} · Sewing: {histReport.line_items.by_section.SewingThread} · Embroidery: {histReport.line_items.by_section.EmbroideryThread} · Labour: {histReport.line_items.by_section.Labour}
                        </div>
                        {!histReport.dry_run && <div className="text-emerald-700 mt-1">✓ created {histReport.created} · updated {histReport.updated}</div>}
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setHistOpen(false); setHistReport(null); }}>Close</Button>
                  <Button variant="outline" onClick={() => runHistoricalImport(true)} disabled={histBusy} data-testid="hist-dry-run-btn">
                    {histBusy ? "…" : "Dry Run"}
                  </Button>
                  <Button onClick={() => runHistoricalImport(false)} disabled={histBusy || !histReport?.dry_run} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="hist-commit-btn">
                    {histBusy ? "Importing…" : "Import Now"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {canCreate && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-brand hover:bg-brand-dark text-white" data-testid="new-enquiry-btn"><Plus className="h-4 w-4 mr-2" /> New Enquiry</Button>
              </DialogTrigger>
              <DialogContent data-testid="new-enquiry-dialog">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><PenSquare className="h-5 w-5 text-brand" /> Create Enquiry Manually</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Enquiry Number</label>
                    <Input value={form.enquiry_number} onChange={(e) => setForm({ ...form, enquiry_number: e.target.value })} placeholder="Auto-generated if blank (e.g. ENQ-2026-0143)" data-testid="new-enquiry-number" className="mt-1" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Buyer <span className="text-rose-600">*</span></label>
                    <Input value={form.buyer} onChange={(e) => setForm({ ...form, buyer: e.target.value })} placeholder="e.g. H&M" list="buyer-options" data-testid="new-enquiry-buyer" className="mt-1" />
                    <datalist id="buyer-options">{buyers.map((b) => <option key={b.id} value={b.name} />)}</datalist>
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Buyer Style Number <span className="text-rose-600">*</span></label>
                    <Input value={form.buyer_style} onChange={(e) => setForm({ ...form, buyer_style: e.target.value })} placeholder="e.g. HM-24-POLO-BLU" data-testid="new-enquiry-buyer-style" className="mt-1" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Notes / Subject</label>
                    <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional — mirrors the email subject line" data-testid="new-enquiry-notes" className="mt-1" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                  <Button onClick={doCreate} disabled={createBusy} className="bg-brand hover:bg-brand-dark text-white" data-testid="new-enquiry-confirm">
                    {createBusy ? "Creating…" : "Create Enquiry"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* KPI Dashboard — click a tile to filter the table below. Click again to clear. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="enquiry-kpi-dashboard">
        {(() => {
          const s = stats || { total: 0, closed: 0, open: 0, timely_closed: 0, timely_assessable: 0, timely_pct: 0 };
          const cards = [
            { key: "total",  filterKey: "all",    label: "Total Enquiries", value: s.total, sub: "All time, all sources", Icon: ClipboardList, tone: "bg-slate-50 border-slate-200 text-slate-700", iconTone: "text-slate-500", activeRing: "ring-slate-400" },
            { key: "closed", filterKey: "closed", label: "Closed", value: s.closed, sub: "Costing sent for approval", Icon: CheckCircle2, tone: "bg-emerald-50 border-emerald-200 text-emerald-800", iconTone: "text-emerald-600", activeRing: "ring-emerald-500" },
            { key: "open",   filterKey: "open",   label: "Open", value: s.open, sub: "Awaiting CAD / Production / Merch", Icon: Timer, tone: "bg-amber-50 border-amber-200 text-amber-900", iconTone: "text-amber-600", activeRing: "ring-amber-500" },
            { key: "timely", filterKey: "timely", label: "Timely Closure", value: `${s.timely_closed} · ${s.timely_pct}%`, sub: s.closed ? `of ${s.closed} closed (assessable: ${s.timely_assessable})` : "No closed enquiries yet", Icon: CalendarClock, tone: "bg-[#7C1D3A]/5 border-[#7C1D3A]/20 text-[#7C1D3A]", iconTone: "text-[#7C1D3A]", activeRing: "ring-[#7C1D3A]" },
          ];
          return cards.map((c) => {
            const isActive = kpiFilter === c.filterKey && c.filterKey !== "all";
            const onClickTile = () => {
              // "Total" tile resets everything. Others toggle.
              if (c.filterKey === "all") { setKpiFilter("all"); return; }
              setKpiFilter((prev) => prev === c.filterKey ? "all" : c.filterKey);
            };
            return (
              <Card
                key={c.key}
                role="button"
                tabIndex={0}
                onClick={onClickTile}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClickTile(); } }}
                className={`p-4 rounded-md border ${c.tone} cursor-pointer transition-shadow hover:shadow-md hover:border-current/40 focus:outline-none focus-visible:ring-2 ${isActive ? `ring-2 ${c.activeRing} shadow-md` : ""}`}
                data-testid={`kpi-card-${c.key}`}
                aria-pressed={isActive}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[10.5px] font-mono uppercase tracking-widest opacity-70">{c.label}</div>
                    <div className="mt-1 font-heading font-bold text-2xl tracking-tight" data-testid={`kpi-value-${c.key}`}>{c.value}</div>
                    <div className="text-[11.5px] opacity-70 mt-0.5">{c.sub}</div>
                  </div>
                  <c.Icon className={`h-5 w-5 shrink-0 ${c.iconTone}`} />
                </div>
              </Card>
            );
          });
        })()}
      </div>

      {/* Filters */}
      <Card className="p-4 border-slate-200 rounded-md bg-white">
        {kpiFilter !== "all" && (
          <div className="mb-3 flex items-center gap-2 text-[12.5px]" data-testid="enquiry-kpi-active-chip">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700 font-mono uppercase tracking-wider">
              Filtering by:&nbsp;<b className="font-semibold not-italic normal-case">
                {kpiFilter === "closed" && "Closed"}
                {kpiFilter === "open" && "Open"}
                {kpiFilter === "timely" && "Timely Closure"}
              </b>
            </span>
            <button
              type="button"
              onClick={() => setKpiFilter("all")}
              data-testid="enquiry-kpi-clear-btn"
              className="text-slate-500 hover:text-brand underline underline-offset-2"
            >
              Clear
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search enquiry, buyer, style..."
              data-testid="enquiries-search"
              className="w-full h-10 pl-9 pr-3 rounded-md bg-slate-50 border border-slate-200 text-sm outline-none focus:bg-white focus:border-slate-300"
            />
          </div>
          <Select value={buyer} onValueChange={setBuyer}>
            <SelectTrigger data-testid="enquiries-filter-buyer"><SelectValue placeholder="Buyer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All buyers</SelectItem>
              {buyers.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="enquiries-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-[12.5px] text-slate-500 self-center">{filtered.length} enquir{filtered.length === 1 ? "y" : "ies"}</div>
        </div>
      </Card>

      {/* Table — simplified per spec: no grand total */}
      <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="opsiq-table">
            <thead>
              <tr>
                <th onClick={() => toggleSort("enquiry_number")} className="cursor-pointer select-none">Enquiry #{sortIcon("enquiry_number")}</th>
                <th onClick={() => toggleSort("buyer")} className="cursor-pointer select-none">Buyer{sortIcon("buyer")}</th>
                <th onClick={() => toggleSort("buyer_style")} className="cursor-pointer select-none">Buyer Style{sortIcon("buyer_style")}</th>
                <th onClick={() => toggleSort("target_date")} className="cursor-pointer select-none" style={{ width: 140 }}>Target Date{sortIcon("target_date")}</th>
                <th onClick={() => toggleSort("created_at")} className="cursor-pointer select-none" style={{ width: 200 }}>Initiated (IST){sortIcon("created_at")}</th>
                <th onClick={() => toggleSort("updated_at")} className="cursor-pointer select-none" style={{ width: 200 }}>Last Updated (IST){sortIcon("updated_at")}</th>
                <th onClick={() => toggleSort("version")} className="cursor-pointer select-none" style={{ width: 80 }}>Ver{sortIcon("version")}</th>
                <th onClick={() => toggleSort("source")} className="cursor-pointer select-none" style={{ width: 100 }}>Source{sortIcon("source")}</th>
                <th style={{ width: 210 }}>Status</th>
                <th onClick={() => toggleSort("attachments")} className="cursor-pointer select-none" style={{ width: 110 }}>Attach{sortIcon("attachments")}</th>
                {isAdmin && <th style={{ width: 60 }}></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} data-testid={`enquiry-row-${e.enquiry_number}`} onClick={() => nav(`/enquiries/${e.id}`)} className="cursor-pointer">
                  <td className="font-mono font-semibold text-brand">{e.enquiry_number}</td>
                  <td className="font-semibold">{e.buyer}</td>
                  <td className="text-slate-600">{e.buyer_style}</td>
                  <td className="font-mono text-[12px]" data-testid={`enq-target-date-${e.enquiry_number}`}>
                    {isAdmin && editingTargetId === e.id ? (
                      <div className="flex items-center gap-1" onClick={(ev) => ev.stopPropagation()}>
                        <Input
                          type="date"
                          value={targetDraft}
                          onChange={(ev) => setTargetDraft(ev.target.value)}
                          className="h-7 w-[132px] text-[12px] px-1.5"
                          data-testid={`enq-target-edit-${e.enquiry_number}`}
                        />
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-700 hover:bg-emerald-50" onClick={() => saveTargetDate(e)} data-testid={`enq-target-save-${e.enquiry_number}`}>
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:bg-slate-100" onClick={() => setEditingTargetId(null)} data-testid={`enq-target-cancel-${e.enquiry_number}`}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : e.target_date ? (
                      <span className={`inline-flex items-center gap-1 ${isOverdue(e.target_date, e.sent_for_approval_at) ? "text-rose-700 font-semibold" : "text-slate-700"}`}>
                        <CalendarClock className="h-3 w-3 opacity-70" /> {fmtDate(e.target_date)}
                        {isAdmin && (
                          <button
                            className="ml-1 text-slate-400 hover:text-brand"
                            title="Edit target date"
                            data-testid={`enq-target-editbtn-${e.enquiry_number}`}
                            onClick={(ev) => { ev.stopPropagation(); setEditingTargetId(e.id); setTargetDraft((e.target_date || "").slice(0, 10)); }}
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    ) : (
                      <span className="text-slate-400 inline-flex items-center gap-1">
                        —
                        {isAdmin && (
                          <button
                            className="ml-1 text-slate-400 hover:text-brand"
                            title="Set target date"
                            data-testid={`enq-target-editbtn-${e.enquiry_number}`}
                            onClick={(ev) => { ev.stopPropagation(); setEditingTargetId(e.id); setTargetDraft(""); }}
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="text-slate-600 font-mono text-[12px]">{fmtIst(e.created_at || e.received_date)}</td>
                  <td className="text-slate-600 font-mono text-[12px]">{fmtIst(e.updated_at || e.received_date)}</td>
                  <td className="font-mono font-semibold text-slate-700">v{e.version || 1}</td>
                  <td>
                    {e.source === "manual" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[11.5px] font-semibold border border-indigo-200"><PenSquare className="h-3 w-3" /> Manual</span>
                    ) : e.source === "sheet" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[11.5px] font-semibold border border-emerald-200"><Sheet className="h-3 w-3" /> Sheet</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[11.5px] font-semibold border border-slate-200"><Mail className="h-3 w-3" /> Email</span>
                    )}
                  </td>
                  <td><StatusCell e={e} /></td>
                  <td>
                    <div className="inline-flex items-center gap-1.5 text-[12px] text-slate-600" data-testid={`enq-att-count-${e.enquiry_number}`}>
                      <Paperclip className="h-3.5 w-3.5" /> {(e.attachments || []).length}
                    </div>
                  </td>
                  {isAdmin && (
                    <td onClick={(ev) => ev.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`enq-delete-${e.enquiry_number}`}
                        onClick={async () => {
                          if (!window.confirm(`Delete ${e.enquiry_number}? This cannot be undone.`)) return;
                          try {
                            await api.delete(`/enquiries/${e.id}`);
                            toast.success(`Deleted ${e.enquiry_number}`);
                            load();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Delete failed");
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 11 : 10} className="text-center py-12 text-slate-500">No enquiries match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
